// The tablet's station: chosen once and remembered on this device (T1). It is
// a per-device convenience, so it lives in localStorage; a private window
// that refuses storage just asks again.
import { useCallback, useSyncExternalStore } from 'react';

const KEY = 'mist.shopfloor.station';
const listeners = new Set<() => void>();

function read(): string | null {
    try {
        return window.localStorage.getItem(KEY);
    } catch {
        return null;
    }
}

function write(station: string | null): void {
    try {
        if (station === null) window.localStorage.removeItem(KEY);
        else window.localStorage.setItem(KEY, station);
    } catch {
        // Storage refused: the choice lasts this page view only.
    }
    memory = station;
    listeners.forEach((listener) => {
        listener();
    });
}

let memory: string | null = null;

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    const onStorage = (event: StorageEvent) => {
        if (event.key === KEY) listener();
    };
    window.addEventListener('storage', onStorage);
    return () => {
        listeners.delete(listener);
        window.removeEventListener('storage', onStorage);
    };
}

/** The station this tablet is at, and a way to change it. */
export function useStation(): [string | null, (station: string | null) => void] {
    const station = useSyncExternalStore(
        subscribe,
        () => read() ?? memory,
        () => null,
    );
    const set = useCallback((next: string | null) => {
        write(next);
    }, []);
    return [station, set];
}
